import {
  CalendarHeader,
  CalendarIcon,
  CustomDropdown,
  FilterBar,
  FilterPill,
  formatCurrencyOption,
  formatPaymentMethodOption,
  Input,
  MMButton,
  SelectAccount
} from '@components'
import { ClearAll } from '@icons'
import cn from '@integrations/classnames'
import type { DateInputHeaderProps, SelectOption as IntegrationOption } from '@integrations/core'
import type { MouseEvent } from '@integrations/react'
import { useMemo, useState } from '@integrations/react'
import { useLocation } from '@integrations/routing'
import { applyDateMask, blockNonDateKey, DATE_INPUT_FLOOR, isTypedDateInvalid } from '@transactions/utils/dateInput'
import { normalizeLanguageCode } from '@utils/language'
import {
  getTransactionStatusOptions,
  selectCurrentLanguage,
  useAppSelector,
  useCurrencies,
  usePaymentMethods
} from 'mmfintech-backend-api'
import type { AccountBalanceResponse } from 'mmfintech-backend-api/types'
import { checkSingleValue, isValidArray, isValidString, tr } from 'mmfintech-commons'
import { MMCheckboxGroup } from 'mmfintech-portal-commons'

import styles from './TransactionsFilter.module.scss'

function multiValueSummary(option: IntegrationOption, index: number, values: readonly IntegrationOption[]) {
  if (index > 0) return null
  return values.length === 1
    ? option.label
    : tr('FRONTEND.TRANSACTIONS.FILTER.SELECTED_COUNT', '{{COUNT}} selected', { COUNT: values.length })
}

interface Props {
  filter: any
  accounts?: AccountBalanceResponse[]
  selectedAccount?: AccountBalanceResponse
  onSelectAccount?: (account: AccountBalanceResponse) => void
}

function TransactionsFilter({ filter, accounts, selectedAccount, onSelectAccount }: Props) {
  const selectedLanguage = normalizeLanguageCode(useAppSelector(selectCurrentLanguage))

  // The calendars list only the years and months their field accepts, inside the calendar (not the browser's own list)
  // the filter keeps a date as a Date, an ISO string or a timestamp (the date input reads all three)
  function asDate(value: unknown, fallback: Date) {
    const date =
      value instanceof Date || typeof value === 'number' || (typeof value === 'string' && value !== '')
        ? new Date(value)
        : null
    return date && !Number.isNaN(date.getTime()) ? date : fallback
  }

  function renderHeader(minDate: Date, maxDate: Date) {
    return (headerProps: DateInputHeaderProps) => (
      <CalendarHeader {...headerProps} minDate={minDate} maxDate={maxDate} locale={selectedLanguage} />
    )
  }
  const location = useLocation()
  const queryParameters = new URLSearchParams(location.search)
  const batchFileId = queryParameters.get('batchFileId')

  const [datePickerKey, setDatePickerKey] = useState(0)

  // What is typed in a date field and whether it is a valid date. The date input only reports valid dates, so
  // typed text that is not one (or is outside the limits) would otherwise stay in the field with no explanation.
  const [invalidDates, setInvalidDates] = useState({ from: false, to: false })

  const { currencyOptionsSimple } = useCurrencies()
  const { paymentMethodOptions } = usePaymentMethods()

  const anyLabel = tr('FRONTEND.TRANSACTIONS.FILTER.ANY', 'Any')
  const minLabel = tr('FRONTEND.TRANSACTIONS.FILTER.MIN', 'Min')
  const maxLabel = tr('FRONTEND.TRANSACTIONS.FILTER.MAX', 'Max')

  // react-select calls preventDefault() on its mousedown, and the date picker's outside-click handler ignores
  // prevented events, so its calendar would stay open next to a select menu. Remounting the inputs closes it.
  function closeDatePickers(event: MouseEvent<HTMLElement>) {
    const target = event.target as Element

    if (!target.closest('.filter-dates, .react-datepicker') && document.querySelector('.react-datepicker')) {
      setDatePickerKey((key) => key + 1)
    }
  }

  function getDateLimits(name: 'from' | 'to') {
    const today = new Date()
    const from = new Date(filter.values.get('from'))
    const to = new Date(filter.values.get('to'))

    return name === 'from'
      ? { min: DATE_INPUT_FLOOR, max: Number.isNaN(to.getTime()) ? today : to }
      : { min: Number.isNaN(from.getTime()) ? DATE_INPUT_FLOOR : from, max: today }
  }

  function setDateInvalid(name: 'from' | 'to', invalid: boolean) {
    setInvalidDates((current) => (current[name] === invalid ? current : { ...current, [name]: invalid }))
  }

  function resetDateErrors() {
    setInvalidDates({ from: false, to: false })
  }

  // Typed text: add the slashes, then flag it once it is long enough to be a finished date and is not a valid one.
  function handleDateTyped(name: 'from' | 'to', event?: { target?: unknown }) {
    const input = event?.target as HTMLInputElement | undefined
    if (input) applyDateMask(input)

    const text = input?.value ?? ''
    const { min, max } = getDateLimits(name)

    setDateInvalid(name, isTypedDateInvalid(text, min, max))
  }

  function getCurrencyOptions() {
    return [{ value: '', label: tr('FRONTEND.TRANSACTIONS.FILTER.ALL_CURRENCIES', 'All') }, ...currencyOptionsSimple]
  }

  const statusOptions = useMemo(() => getTransactionStatusOptions(), [])

  const items = useMemo(
    () => filter.meta.map(({ name: key, label, localizationKey }) => ({ key, label, localizationKey })),
    [filter.meta]
  )

  function has(name: string) {
    const value = filter.values.get(name)
    return isValidArray(value) || isValidString(value) || (typeof value === 'number' && !Number.isNaN(value))
  }

  // The amount inputs are sized to their content (not a fixed width) so the pill's own gap spaces "Min – Max" evenly
  // while a long value still stays fully visible as it is typed.
  function amountSize(value: unknown, placeholder: string) {
    const text = String(value ?? '')
    return text ? text.length + 1 : placeholder.length
  }

  function setAmount(name: string, value: string) {
    filter.values.set(name, checkSingleValue(value, { validation: 'float' }))
    filter.values.reload()
  }

  return (
    <FilterBar className={'filter-wrapper'} onMouseDownCapture={closeDatePickers}>
      <FilterBar.Row className={'filters-container'}>
        {onSelectAccount && (
          <FilterPill label={tr('FRONTEND.TRANSACTIONS.ACCOUNT_LABEL', 'Account')} active={Boolean(selectedAccount)}>
            <SelectAccount
              compact
              showAllAccounts
              accounts={accounts}
              selectedAccount={selectedAccount}
              setSelectedAccount={onSelectAccount}
            />
          </FilterPill>
        )}

        <FilterPill label={<CalendarIcon />} className={cn('filter-dates')} data-test='filter-date-range'>
          <span className={cn(styles['filter-dates__group'], 'filter-dates__group')}>
            <span className={cn(styles['filter-dates__label'], 'filter-dates__label')}>
              {tr('FRONTEND.TRANSACTIONS.FILTER.FROM', 'From')}
            </span>
            <Input
              key={`from-${datePickerKey}`}
              type='date'
              name='from'
              selectedLanguage={selectedLanguage}
              minDate={DATE_INPUT_FLOOR}
              maxDate={filter.values.get('to') || new Date()}
              selectsStart
              startDate={filter.values.get('from')}
              endDate={filter.values.get('to')}
              strictParsing
              renderHeader={renderHeader(DATE_INPUT_FLOOR, asDate(filter.values.get('to'), new Date()))}
              calendarClassName='calendar-with-panels'
              aria-invalid={invalidDates.from || undefined}
              onKeyDown={blockNonDateKey}
              onTextChange={(event) => handleDateTyped('from', event)}
              // the message goes away with the text it was about: a date picked in the calendar replaces it, and when
              // the field is left the date input drops invalid text and goes back to its last valid date
              onDateSelect={() => setDateInvalid('from', false)}
              onCalendarOpen={() => setDateInvalid('from', false)}
              onCalendarClose={() => setDateInvalid('from', false)}
              {...filter.values.registerInput('from')}
              parentClassName='transaction-filter-date'
              data-test='filter-from-date'
              hideErrorLine
              overlayId='datepicker-portal'
              placement='bottom'
              keepInViewport
            />
            {invalidDates.from && (
              <span
                role='alert'
                className={cn(styles['filter-dates__error'], 'filter-dates__error')}
                data-test='filter-from-date-error'
              >
                {tr('FRONTEND.TRANSACTIONS.FILTER.INVALID_DATE', 'Invalid date')}
              </span>
            )}
          </span>
          <span className={cn(styles['filter-dates__group'], 'filter-dates__group')}>
            <span className={cn(styles['filter-dates__arrow'], 'filter-dates__arrow')}>→</span>
            <span className={cn(styles['filter-dates__label'], 'filter-dates__label')}>
              {tr('FRONTEND.TRANSACTIONS.FILTER.TO', 'To')}
            </span>
            <Input
              key={`to-${datePickerKey}`}
              type='date'
              name='to'
              selectedLanguage={selectedLanguage}
              minDate={filter.values.get('from') || DATE_INPUT_FLOOR}
              selectsEnd
              startDate={filter.values.get('from')}
              endDate={filter.values.get('to')}
              maxDate={new Date()}
              strictParsing
              renderHeader={renderHeader(asDate(filter.values.get('from'), DATE_INPUT_FLOOR), new Date())}
              calendarClassName='calendar-with-panels'
              aria-invalid={invalidDates.to || undefined}
              onKeyDown={blockNonDateKey}
              onTextChange={(event) => handleDateTyped('to', event)}
              onDateSelect={() => setDateInvalid('to', false)}
              onCalendarOpen={() => setDateInvalid('to', false)}
              onCalendarClose={() => setDateInvalid('to', false)}
              {...filter.values.registerInput('to')}
              parentClassName='transaction-filter-date'
              data-test='filter-to-date'
              hideErrorLine
              overlayId='datepicker-portal'
              placement='bottom'
              keepInViewport
            />
            {invalidDates.to && (
              <span
                role='alert'
                className={cn(styles['filter-dates__error'], 'filter-dates__error')}
                data-test='filter-to-date-error'
              >
                {tr('FRONTEND.TRANSACTIONS.FILTER.INVALID_DATE', 'Invalid date')}
              </span>
            )}
          </span>
        </FilterPill>

        {filter.isVisible('status') && (
          <FilterPill label={tr('FRONTEND.TRANSACTIONS.FILTER.STATUS', 'Status')} active={has('statuses')}>
            <Input
              type='select'
              name='statuses'
              placeholder={tr('FRONTEND.TRANSACTIONS.FILTER.ALL', 'All')}
              {...filter.values.registerInput('statuses')}
              parentClassName='transaction-filter-status transaction-filter-select'
              options={statusOptions}
              data-test='filter-status'
              hideErrorLine
              isSearchable={false}
              isMultiSelect={true}
              presentation={{ renderMultiValue: multiValueSummary, hideSelectedOptions: false }}
              dropdownPortal
            />
          </FilterPill>
        )}

        {filter.isVisible('currency') && (
          <FilterPill label={tr('FRONTEND.TRANSACTIONS.FILTER.CURRENCY', 'Currency')} active={has('currency')}>
            <Input
              type='select'
              name='currency'
              {...filter.values.registerInput('currency')}
              parentClassName='transaction-filter-field-md transaction-filter-select'
              options={getCurrencyOptions()}
              renderOption={formatCurrencyOption}
              data-test='filter-currency'
              hideErrorLine
              dropdownPortal
            />
          </FilterPill>
        )}

        {filter.isVisible('paymentMethod') && (
          <FilterPill
            label={tr('FRONTEND.TRANSACTIONS.FILTER.PAYMENT_METHOD', 'Payment method')}
            active={has('paymentMethods')}
          >
            <Input
              type='select'
              name='paymentMethods'
              placeholder={tr('FRONTEND.TRANSACTIONS.FILTER.ALL', 'All')}
              {...filter.values.registerInput('paymentMethods')}
              parentClassName='transaction-filter-payment-method'
              options={paymentMethodOptions}
              renderOption={formatPaymentMethodOption}
              data-test='filter-payment-method'
              hideErrorLine
              isMultiSelect
              isSearchable={false}
              presentation={{ renderMultiValue: multiValueSummary, hideSelectedOptions: false }}
              dropdownPortal
            />
          </FilterPill>
        )}

        {filter.isVisible('amount') && (
          <FilterPill
            label={tr('FRONTEND.TRANSACTIONS.FILTER.AMOUNT', 'Amount')}
            active={has('fromAmount') || has('toAmount')}
            className={'filter-amount'}
          >
            <FilterPill.Input
              name='fromAmount'
              inputMode='decimal'
              placeholder={minLabel}
              aria-label={tr('FRONTEND.TRANSACTIONS.FILTER.FROM_AMOUNT', 'From Amount')}
              size={amountSize(filter.values.get('fromAmount'), minLabel)}
              value={filter.values.get('fromAmount') ?? ''}
              onChange={(e) => setAmount('fromAmount', e.target.value)}
              data-test='filter-from-amount'
            />
            <span className={cn(styles['filter-dates__arrow'], 'filter-dates__arrow')}>–</span>
            <FilterPill.Input
              name='toAmount'
              inputMode='decimal'
              placeholder={maxLabel}
              aria-label={tr('FRONTEND.TRANSACTIONS.FILTER.TO_AMOUNT', 'To Amount')}
              size={amountSize(filter.values.get('toAmount'), maxLabel)}
              value={filter.values.get('toAmount') ?? ''}
              onChange={(e) => setAmount('toAmount', e.target.value)}
              data-test='filter-to-amount'
            />
          </FilterPill>
        )}
      </FilterBar.Row>

      <FilterBar.Row className={'filter-text-row'}>
        {filter.isVisible('id') && (
          <FilterPill label={tr('FRONTEND.TRANSACTIONS.FILTER.TRANSACTION_ID', 'ID')} active={has('id')}>
            <Input
              type='number'
              name='id'
              placeholder={anyLabel}
              {...filter.values.registerInput('id')}
              parentClassName='transaction-filter-id'
              data-test='filter-id'
              hideErrorLine
            />
          </FilterPill>
        )}
        {filter.isVisible('reference') && (
          <FilterPill
            label={tr('FRONTEND.TRANSACTIONS.FILTER.REFERENCE', 'Reference')}
            active={has('reference')}
            className={'filter-text'}
            search
          >
            <Input
              type='text'
              name='reference'
              placeholder={anyLabel}
              {...filter.values.registerInput('reference')}
              parentClassName='transaction-filter-field-md'
              data-test='filter-reference'
              hideErrorLine
            />
          </FilterPill>
        )}
        {filter.isVisible('foreignId') && (
          <FilterPill
            label={tr('FRONTEND.TRANSACTIONS.FILTER.FOREIGN_ID', 'Provided ID')}
            active={has('foreignTransactionId')}
            className={'filter-text'}
            search
          >
            <Input
              type='text'
              name='foreignTransactionId'
              placeholder={anyLabel}
              {...filter.values.registerInput('foreignTransactionId')}
              parentClassName='transaction-filter-field-md'
              data-test='filter-foreign-id'
              hideErrorLine
            />
          </FilterPill>
        )}
        {(filter.isVisible('batchFileId') || batchFileId) && (
          <FilterPill
            label={tr('FRONTEND.TRANSACTIONS.FILTER.BATCH_FILE_ID', 'Batch ID')}
            active={has('batchFileId')}
            className={'filter-text'}
            search
          >
            <Input
              type='text'
              name='batchFileId'
              placeholder={anyLabel}
              {...filter.values.registerInput('batchFileId')}
              parentClassName='transaction-filter-field-md'
              data-test='filter-batch-file-id'
              hideErrorLine
            />
          </FilterPill>
        )}
        {filter.isVisible('customer') && (
          <FilterPill
            label={tr('FRONTEND.TRANSACTIONS.FILTER.CUSTOMER_EMAIL', 'Customer Email')}
            active={has('description')}
            className={'filter-text'}
            search
          >
            <Input
              type='text'
              name='description'
              placeholder={anyLabel}
              {...filter.values.registerInput('description')}
              parentClassName='transaction-filter-customer'
              data-test='filter-customer'
              hideErrorLine
            />
          </FilterPill>
        )}

        <FilterBar.End className={'filter-buttons'}>
          <CustomDropdown
            caption={tr('FRONTEND.TRANSACTIONS.FILTER.FILTERS_BUTTON', 'Filters')}
            dropdown={
              <div className={cn(styles['filter-checkbox-group-container'], 'filter-checkbox-group-container')}>
                <MMCheckboxGroup
                  items={items}
                  getChecked={filter.isVisible}
                  setChecked={(name: string) => {
                    filter.setVisible(name)
                    filter.values.reload()
                  }}
                  header={tr('FRONTEND.TRANSACTIONS.FILTER.CHOOSE_FILTERS', 'Choose filters')}
                  footer={
                    <MMButton
                      type='button'
                      variant='round-filter'
                      leftIcon={ClearAll}
                      caption={tr('FRONTEND.TRANSACTIONS.FILTER.CLEAR_FILTERS_BUTTON', 'Clear Filters')}
                      onClick={() => {
                        filter.clearFilters()
                        resetDateErrors()
                        filter.delayedReload.execute()
                      }}
                      fullWidth
                    />
                  }
                />
              </div>
            }
          />
          <MMButton
            type='button'
            variant='secondary'
            caption={tr('FRONTEND.TRANSACTIONS.FILTER.CLEAR_FILTERS_BUTTON', 'Clear Filters')}
            onClick={() => {
              filter.clearAll()
              resetDateErrors()
            }}
            data-test='button-clear-filters'
          />
        </FilterBar.End>
      </FilterBar.Row>
    </FilterBar>
  )
}

export default TransactionsFilter
